import { z } from "zod";
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { accessAPI, queries, errorMessage } from "../lib/api";
import { RequestFailure } from "../components/RequestFailure";
import { isSyncFresh, useHealthClock } from "../lib/health";
import { styles } from "../lib/styles";
import { NavIcon, Drawer, EmptyState, PageHeader, ConfirmAction } from "../components/Workspace";
import { ServerForm } from "../components/ServerForm";

export const Route = createFileRoute("/servers/$serverId")({
  validateSearch: z.object({
    q: z.string().max(200).optional().catch(undefined),
    auth: z.enum(["all", "ready", "attention"]).optional().catch(undefined),
    page: z.coerce.number().int().min(0).max(100000).optional().catch(undefined),
  }),
  component: ServerDetail,
});
function ServerDetail() {
  const { serverId } = Route.useParams();
  const listing = Route.useSearch();
  const now = useHealthClock();
  const [notice, setNotice] = useState("");
  const state = useQuery(queries.state());
  const [editing, setEditing] = useState(false);
  const cache = useQueryClient();
  const save = useMutation({
    mutationFn: accessAPI.updateServer,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["access"] }),
  });
  const server = state.data?.servers.find((item) => item.id === serverId);
  const deployment = state.data?.deployments.find((item) => item.server_id === serverId);
  if (state.isPending) return <p aria-busy="true">Loading server…</p>;
  if (state.error && !state.data)
    return (
      <RequestFailure
        error={state.error}
        retry={() => {
          void state.refetch();
        }}
        pending={state.isFetching}
      />
    );
  if (!server)
    return (
      <EmptyState
        title="Server not found"
        description="This server may have been removed or is no longer available."
        action={<Link to="/servers">Back to servers</Link>}
      />
    );
  return (
    <>
      <Link to="/servers" search={listing} className="breadcrumb">
        <NavIcon name="ArrowLeft" /> Servers
      </Link>
      <PageHeader
        title={server.alias}
        description={`${server.login}@${server.address}:${server.port}`}
        action={
          <Button
            label="Edit server"
            variant="primary"
            isDisabled={!!state.error}
            onClick={() => setEditing(true)}
          />
        }
      />
      {notice && <output {...stylex.props(styles.notice)}>{notice}</output>}
      <RequestFailure
        error={state.error}
        retry={() => {
          void state.refetch();
        }}
        pending={state.isFetching}
      />
      <div className="detail-grid">
        <div>
          <section data-workspace-panel {...stylex.props(styles.panel)}>
            <div className="section-heading">
              <h2>Authorization</h2>
              <span
                {...stylex.props(
                  styles.status,
                  server.enabled &&
                    isSyncFresh(state.data!, now) &&
                    deployment?.status === "applied" &&
                    styles.applied,
                )}
              >
                {!server.enabled
                  ? "Disabled"
                  : !isSyncFresh(state.data!, now)
                    ? "Sync stale — readiness unverified"
                    : (deployment?.status ?? "Not applied")}
              </span>
            </div>
            {deployment?.error && (
              <p role="alert" {...stylex.props(styles.error)}>
                {deployment.error}
              </p>
            )}
            <dl className="definition-list">
              <dt>Applied identities</dt>
              <dd>
                {deployment?.applied.length
                  ? deployment.applied.map((email) => <p key={email}>{email}</p>)
                  : "No identities applied"}
              </dd>
              <dt>Updated</dt>
              <dd>
                {deployment ? new Date(deployment.updated_at).toLocaleString() : "Not yet deployed"}
              </dd>
            </dl>
            <div className="section-heading">
              <h2>Group rules</h2>
              <Link to="/rules" search={{ view: "groups" }}>
                Manage access
              </Link>
            </div>
            {state.data?.rules
              .filter((rule) => rule.server_id === serverId)
              .map((rule) => (
                <div className="list-row" key={rule.id}>
                  <strong>{rule.group_name}</strong>
                  <span {...stylex.props(styles.status)}>
                    {rule.enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              ))}
          </section>
          <section data-workspace-panel {...stylex.props(styles.panel)}>
            <h2 {...stylex.props(styles.panelHeading)}>Connection</h2>
            <pre {...stylex.props(styles.code)}>ssh {server.alias}</pre>
            <p {...stylex.props(styles.small)}>
              Keep SSBD Access running while you work. Access remains subject to your applied
              grants.
            </p>
          </section>
        </div>
        <section data-workspace-panel {...stylex.props(styles.panel)}>
          <h2 {...stylex.props(styles.panelHeading)}>Configuration</h2>
          <dl className="definition-list">
            <dt>Server ID</dt>
            <dd className="mono">{server.id}</dd>
            <dt>NetBird peer</dt>
            <dd className="mono">{server.peer_id}</dd>
            <dt>Account</dt>
            <dd>{server.login}</dd>
            <dt>Provisioning</dt>
            <dd>{server.provision_user}</dd>
            <dt>Host key</dt>
            <dd className="mono">{server.host_key}</dd>
          </dl>
          <div {...stylex.props(styles.actions)}>
            {server.enabled ? (
              <ConfirmAction
                label="Disable server"
                disabled={!!state.error}
                target={server.alias}
                description="This removes managed SSH access after synchronization and can close affected sessions. Recovery SSH remains available."
                onConfirm={() =>
                  save.mutateAsync({ ...server, enabled: false }).then(() => {
                    setNotice(
                      `${server.alias} disable was saved. Synchronization will remove managed access.`,
                    );
                  })
                }
                busy={save.isPending}
              />
            ) : (
              <Button
                label="Enable server"
                isDisabled={!!state.error}
                onClick={() => save.mutate({ ...server, enabled: true })}
                isLoading={save.isPending}
              />
            )}
          </div>
          {save.error && (
            <p role="alert" {...stylex.props(styles.error)}>
              {errorMessage(save.error)}
            </p>
          )}
          <p {...stylex.props(styles.small)}>
            Changes take effect after successful synchronization. Recovery SSH is preserved.
          </p>
        </section>
      </div>
      {editing && (
        <Drawer title="Edit server" onClose={() => setEditing(false)}>
          <ServerForm
            server={server}
            onSaved={(saved) => {
              setNotice(
                `${saved.alias} was updated. Synchronize access to apply the configuration.`,
              );
              setEditing(false);
            }}
          />
        </Drawer>
      )}
    </>
  );
}
